import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { envPath, textOf, type AchievementsView, type GameConfig, type LeaderboardView } from '../api';
import { Empty, ErrorState, num, PageHeader, shortId, Spinner } from '../components/ui';
import { usePagedResource, useResource } from '../hooks';
import { LoadMore } from '../components/LoadMore';
import { formatNumber, translate, useT, type MessageKey } from '../i18n';
import { useShell } from './ProjectShell';
import { FeatureHint } from '../components/FeatureHint';
import { pushCommand } from '../featureToggle';
import { projectEyebrow } from '../projectNav';

function formatValue(v: number, format: string) {
  // 秒の小数は locale の小数点で（単位 s はタイム表記の慣習として両言語共通）
  if (format === 'time_ms') return `${formatNumber(v / 1000, { minimumFractionDigits: 3, maximumFractionDigits: 3 })}s`;
  if (format === 'time_s') return `${Math.floor(v / 60)}:${String(Math.floor(v % 60)).padStart(2, '0')}`;
  return num(v);
}

/** config の reset 値（daily / weekly / monthly / none）→ 表示。未知の値はそのまま差し込む */
const RESET_KEYS: Record<string, MessageKey> = { daily: 'lb.reset.daily', weekly: 'lb.reset.weekly', monthly: 'lb.reset.monthly', none: 'lb.reset.never' };
const MISSION_KEYS: Record<string, MessageKey> = { daily: 'ach.mission.daily', weekly: 'ach.mission.weekly', monthly: 'ach.mission.monthly' };
const resetLabel = (reset: string | undefined) => {
  const k = RESET_KEYS[reset ?? 'none'];
  return k ? translate(k) : translate('lb.reset.other', { reset: reset ?? '' });
};
const missionLabel = (reset: string) => {
  const k = MISSION_KEYS[reset];
  return k ? translate(k) : translate('ach.mission.other', { reset });
};

/**
 * スコアを送る SDK の呼び方。stats.add() は集計が sum のステータス専用で、max / min / latest に使うと SDK が
 * wrong_aggregation で断る（packages/sdk/src/gamedata.ts）。以前は常に add を案内していて、雛形の high_score（max）では
 * 案内どおりに書くと失敗した（dashboard-review 2026-09-27）。集計は config の stats から引く（無ければ既定の sum）
 */
function statCall(config: GameConfig | undefined, stat: string | undefined): string {
  const agg = config?.stats?.find((s) => s.key === stat)?.aggregation ?? 'sum';
  return agg === 'sum' ? `kumo.stats.add('${stat ?? ''}', 1)` : `kumo.stats.submit('${stat ?? ''}', score)`;
}

export function LeaderboardsPage() {
  const { project, env } = useShell();
  const base = envPath(project.id, env);
  const cfg = useResource<{ version: number; config: GameConfig }>(`${base}/config`);
  const boards = cfg.data?.config.leaderboards ?? [];
  const [selected, setSelected] = useState<string | null>(null);
  const [period, setPeriod] = useState<'current' | 'previous'>('current');
  useEffect(() => {
    if (boards.length && (!selected || !boards.some((b) => b.key === selected))) setSelected(boards[0]!.key);
  }, [boards, selected]);
  const board = boards.find((b) => b.key === selected);
  // 50 位ずつ「さらに読み込む」で下へ（2026-10-04 console/limits-ui。以前は上位 50 で切れて 51 位から下が見えなかった）
  const lb = usePagedResource<LeaderboardView, 'entries'>(selected ? `${base}/leaderboards/${selected}?limit=50${period === 'previous' ? '&period=previous' : ''}` : null, 'entries');
  const ach = useResource<AchievementsView>(`${base}/achievements`);
  const { t, tr } = useT();

  return (
    <>
      <PageHeader eyebrow={projectEyebrow(project)} title={t('lb.title')} />
      {/* ランキングは stats の送信で埋まり、実績も stats から解除される。どれかが OFF なら、そもそも何も溜まらない */}
      <FeatureHint projectId={project.id} env={env} need={['stats', 'leaderboards', 'achievements']} />
      {cfg.loading && !cfg.data && <Spinner />}
      {cfg.error && <ErrorState error={cfg.error} what={t('nav.config')} onRetry={cfg.reload} />}

      {cfg.data && boards.length === 0 && (
        <Empty title={t('lb.noBoardsTitle')}>
          <p>
            {tr('lb.noBoardsBody', {
              file: <code>kumo.config.json</code>,
              cmd: <code>{pushCommand(env)}</code>,
              link: <Link to="../config">{t('lb.configLink')}</Link>
            })}
          </p>
        </Empty>
      )}

      {boards.length > 0 && (
        <section className="card">
          <div className="tabs" role="tablist" aria-label={t('lb.tabs')}>
            {boards.map((b) => (
              <button
                key={b.key}
                type="button"
                role="tab"
                aria-selected={b.key === selected}
                className={`tab ${b.key === selected ? 'is-active' : ''}`}
                onClick={() => setSelected(b.key)}
              >
                {textOf(b.title) || b.key}
              </button>
            ))}
          </div>
          {board && (
            <div className="lb-meta">
              <span className="pill">{tr('lb.stat', { stat: <span className="mono">{board.stat}</span> })}</span>
              <span className="pill">{board.order === 'asc' ? t('lb.lowestWins') : t('lb.highestWins')}</span>
              <span className="pill">{resetLabel(board.reset)}</span>
              {board.reset && board.reset !== 'none' && (
                <div className="seg" role="group" aria-label={t('lb.season')}>
                  {(['current', 'previous'] as const).map((p) => (
                    <button key={p} type="button" className={`seg-btn ${period === p ? 'is-active' : ''}`} aria-pressed={period === p} onClick={() => setPeriod(p)}>
                      {p === 'current' ? t('lb.thisSeason') : t('lb.lastSeason')}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {lb.loading && !lb.data && <Spinner />}
          {lb.error && <ErrorState error={lb.error} what={t('lb.what')} onRetry={lb.reload} />}
          {lb.data && lb.data.entries.length === 0 && (
            <Empty title={t('lb.noScoresTitle')}>
              <p>
                {tr('lb.noScoresBody', { call: <code>{statCall(cfg.data?.config, board?.stat)}</code>, period: <code>{lb.data.period}</code> })}
              </p>
            </Empty>
          )}
          {lb.data && lb.data.entries.length > 0 && (
            <>
              <p className="small muted">
                {tr('lb.ranked', { count: lb.data.total, period: <span className="mono">{lb.data.period}</span> })}
              </p>
              <ol className="podium">
                {/* key に位置を入れる: ページの間にスコアが動くと、同じ人が 2 つのページに出ることがある（今の順位を見せるので正しい） */}
                {lb.data.entries.map((e, i) => (
                  <li key={`${i}:${e.playerId}`} className={`rank rank-${Math.min(e.rank, 4)}`}>
                    <span className="rank-n">{e.rank}</span>
                    <span className="rank-name">
                      {e.displayName ?? <em className="muted">{t('common.guest')}</em>} <span className="mono muted small">{shortId(e.playerId)}</span>
                    </span>
                    <span className="rank-v mono">{formatValue(e.value, lb.data!.leaderboard.format)}</span>
                  </li>
                ))}
              </ol>
              <LoadMore more={lb.more} />
            </>
          )}
        </section>
      )}

      <section className="card">
        <div className="card-head">
          <h2 className="card-title">{t('ach.title')}</h2>
          {ach.data && <span className="small muted">{t('ach.eligible', { count: ach.data.players })}</span>}
        </div>
        {ach.loading && !ach.data && <Spinner />}
        {ach.error && <ErrorState error={ach.error} what={t('ach.title')} onRetry={ach.reload} />}
        {ach.data && ach.data.achievements.length === 0 && (
          <Empty title={t('ach.emptyTitle')}>
            <p>{tr('ach.emptyBody', { condition: <code>condition</code>, call: <code>kumo.achievements.unlock(key)</code> })}</p>
          </Empty>
        )}
        {ach.data && ach.data.achievements.length > 0 && (
          <ul className="ach-list">
            {ach.data.achievements.map((a) => {
              const pct = ach.data!.players ? (a.unlockedBy / ach.data!.players) * 100 : 0;
              return (
                <li key={a.key} className="ach">
                  <div className="ach-head">
                    <strong>{textOf(a.title)}</strong>
                    {a.hidden && <span className="pill">{t('ach.hidden')}</span>}
                    {a.reset !== 'none' && <span className="pill">{missionLabel(a.reset)}</span>}
                  </div>
                  <div className="ach-sub mono small muted">
                    {a.key}
                    {a.condition ? ` · ${a.condition.stat} ${a.condition.gte !== undefined ? `≥ ${a.condition.gte}` : `≤ ${a.condition.lte}`}` : ` · ${t('ach.manual')}`}
                  </div>
                  <div className="meter" role="img" aria-label={t('ach.meter', { pct: formatNumber(pct, { maximumFractionDigits: 1 }) })}>
                    <span style={{ width: `${Math.min(100, pct)}%` }} />
                  </div>
                  <div className="small">
                    <span className="muted">
                      {tr('ach.unlockedBy', {
                        count: a.unlockedBy,
                        n: <strong className="ach-count">{num(a.unlockedBy)}</strong>,
                        pct: formatNumber(pct, { maximumFractionDigits: pct < 10 ? 1 : 0 })
                      })}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </>
  );
}
